import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from "react";
import axios from "../utils/axiosConfig";
import { authEvents } from "../utils/axiosConfig";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "../utils/toast";

import logger from '../utils/logger';

// Constants
const REFRESH_KEY = 'auth_refresh_pending';
const TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const LAST_ACTIVITY_KEY = 'lastActivity';

// ← Session-Management Konstanten (Vernünftige Werte)
const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 60 Minuten
const ACTIVITY_CHECK_INTERVAL = 5 * 60 * 1000; // 5 Minuten
const SESSION_EXTEND_THRESHOLD = 30 * 60 * 1000; // 30 Minuten
const WARNING_BEFORE_LOGOUT = 5 * 60 * 1000; // 5 Minuten Warnung

// Erweitere Axios Config Type für _retryCount
declare module 'axios' {
  export interface AxiosRequestConfig {
    _retryCount?: number;
    _skipSessionExtend?: boolean;
  }
}

interface AuthContextProps {  
  userRole: string | null; 
  clubId: string | null;
  loading: boolean;
  isInitialized: boolean;
  isLoginInProgress: boolean;
  sessionTimeRemaining: number | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  extendSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userRole, setUserRole] = useState<string | null>(
    sessionStorage.getItem('userRole') || null
  );
  const [clubId, setClubId] = useState<string | null>(
    sessionStorage.getItem('clubId') || null
  ); 
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoginInProgress, setIsLoginInProgress] = useState(false);
  const [lastChecked, setLastChecked] = useState<number>(0);
  
  // Session-Management States
  const [lastActivity, setLastActivity] = useState<number>(() => {
    const stored = localStorage.getItem(LAST_ACTIVITY_KEY);
    return stored ? parseInt(stored) : Date.now();
  });
  
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState<number | null>(() => {
    if (userRole) {
      const now = Date.now();
      const stored = localStorage.getItem(LAST_ACTIVITY_KEY);
      const lastAct = stored ? parseInt(stored) : now;
      const remaining = INACTIVITY_TIMEOUT - (now - lastAct);
      return Math.max(0, remaining);
    }
    return null;
  });
  
  const [warningShown, setWarningShown] = useState(false);
  
  // Refs für Cleanup
  const activityCheckInterval = useRef<NodeJS.Timeout | null>(null);
  const sessionExtendTimeout = useRef<NodeJS.Timeout | null>(null);
  const logoutWarningTimeout = useRef<NodeJS.Timeout | null>(null);
  
  // ← NEU: Ref um häufige Session-Extension Nachrichten zu vermeiden
  const lastExtensionNotification = useRef<number>(0);

  const navigate = useNavigate();
  const location = useLocation();

  React.useMemo(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  // ← Activity Tracking - optimiert ohne Spam
  const updateActivity = useCallback((source: string = 'manual') => {
    const now = Date.now();
    setLastActivity(now);
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
    setWarningShown(false);
    
    // Session-Zeit neu berechnen
    setSessionTimeRemaining(INACTIVITY_TIMEOUT);
    
    // Cancel existing timeouts
    if (logoutWarningTimeout.current) {
      clearTimeout(logoutWarningTimeout.current);
      logoutWarningTimeout.current = null;
    }
    
    // ← NEU: Weniger aggressive Logging
    if (source === 'manual') {
      logger.debug('🔄 Manual activity update');
    }
  }, []);

  // Set auth token in axios headers
  const setAuthToken = useCallback((token: string | null) => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem(TOKEN_KEY);
    }
  }, []);

  // Store refresh token
  const setRefreshToken = useCallback((token: string | null) => {
    if (token) {
      localStorage.setItem(REFRESH_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  }, []);

  // Check if we're on a login-related page
  const isLoginPage = useCallback(() => {
    return location.pathname === '/' || location.pathname === '/login' || location.pathname === '/register'|| location.pathname === '/forgot-password' || location.pathname === '/reset-password';
  }, [location.pathname]);

  // Clear auth data helper
  const clearAuthData = useCallback(() => {
    setUserRole(null);
    setClubId(null);
    setAuthToken(null);
    setRefreshToken(null);
    sessionStorage.removeItem('userRole');
    sessionStorage.removeItem('clubId');
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    setSessionTimeRemaining(null);
    setWarningShown(false);
    
    // Clear all timeouts
    if (activityCheckInterval.current) {
      clearInterval(activityCheckInterval.current);
      activityCheckInterval.current = null;
    }
    if (sessionExtendTimeout.current) {
      clearTimeout(sessionExtendTimeout.current);
      sessionExtendTimeout.current = null;
    }
    if (logoutWarningTimeout.current) {
      clearTimeout(logoutWarningTimeout.current);
      logoutWarningTimeout.current = null;
    }
  }, [setAuthToken, setRefreshToken]);

  // ← Session verlängern - optimiert ohne Spam
  const extendSession = useCallback(async () => {
    if (!userRole || isLoginInProgress) return;
    
    try {
      logger.info('🔄 Extending session manually...');
      await axios.get('/api/auth/check-auth', {
        withCredentials: true,
        timeout: 10000,
        _skipSessionExtend: true
      } as any);
      
      updateActivity('extension');
      
      // ← NEU: Benachrichtigung nur bei manueller Extension und nicht öfter als alle 5 Minuten
      const now = Date.now();
      if (now - lastExtensionNotification.current > 5 * 60 * 1000) {
        toast.success('Session verlängert!', { autoClose: 2000 });
        lastExtensionNotification.current = now;
      }
      
      logger.info('✅ Session extended successfully');
    } catch (error) {
      logger.warn('⚠️ Session extension failed', { error });
      toast.error('Session-Verlängerung fehlgeschlagen');
    }
  }, [userRole, isLoginInProgress, updateActivity]);

  // ← Überprüfung auf Inaktivität - reduzierte Logs
  const checkInactivity = useCallback(async () => {
    if (!userRole || isLoginInProgress || isLoginPage()) return;

    const now = Date.now();
    const timeSinceLastActivity = now - lastActivity;
    const timeRemaining = INACTIVITY_TIMEOUT - timeSinceLastActivity;
    
    setSessionTimeRemaining(Math.max(0, timeRemaining));
    
    // Warnung vor Logout
    if (timeRemaining <= WARNING_BEFORE_LOGOUT && timeRemaining > 0 && !warningShown) {
      setWarningShown(true);
      const minutesLeft = Math.ceil(timeRemaining / (60 * 1000));
      
      logger.warn('⚠️ Session warning:', { minutesLeft });
      
      toast.warning(
        `Deine Sitzung läuft in ${minutesLeft} Minute(n) ab. Bewege die Maus oder klicke irgendwo, um die Sitzung zu verlängern.`,
        {
          autoClose: false,
          closeOnClick: false,
          onClose: () => updateActivity('warning-dismiss')
        }
      );
      
      // Auto-Logout wenn keine Aktivität
      logoutWarningTimeout.current = setTimeout(() => {
        if (Date.now() - lastActivity >= INACTIVITY_TIMEOUT) {
          logger.warn('⏰ Auto-logout due to inactivity');
          toast.error('Sitzung wegen Inaktivität beendet.');
          logout();
        }
      }, WARNING_BEFORE_LOGOUT);
    }
    
    // ← NEU: Automatische Session-Verlängerung nur bei niedrigem Timer, ohne Toast-Spam
    if (timeRemaining > WARNING_BEFORE_LOGOUT && timeRemaining <= SESSION_EXTEND_THRESHOLD) {
      logger.info('🔄 Auto-extending session (threshold reached)');
      try {
        await axios.get('/api/auth/check-auth', {
          withCredentials: true,
          timeout: 10000,
          _skipSessionExtend: true
        } as any);
        updateActivity('auto-extend');
        // ← Kein Toast bei automatischer Verlängerung
      } catch (error) {
        logger.warn('⚠️ Auto session extension failed', { error });
      }
    }
    
    // Sofortiges Logout wenn Zeit abgelaufen
    if (timeRemaining <= 0) {
      logger.warn('⏰ Session expired due to inactivity');
      toast.error('Deine Sitzung ist wegen Inaktivität abgelaufen.');
      await logout();
    }
  }, [userRole, isLoginInProgress, isLoginPage, lastActivity, warningShown, updateActivity]);

  // Handle authentication checking
  const checkAuth = useCallback(async () => {
    // Skip auth check if on login page without existing credentials
    if (isLoginPage() && !userRole && !sessionStorage.getItem('userRole')) {
      setLoading(false);
      setIsInitialized(true);
      return;
    }

    // Skip auth check wenn Login gerade läuft
    if (isLoginInProgress) {
      return;
    }

    try {
      setLoading(true);

      // Throttle auth checks
      const now = Date.now();
      if (lastChecked > 0 && now - lastChecked < 10000 && isInitialized) {
        setLoading(false);
        return;
      }
      
      setLastChecked(now);

      // Get stored token and set in headers if available
      const storedToken = localStorage.getItem(TOKEN_KEY);
      if (storedToken) {
        setAuthToken(storedToken);
      }

      const response = await axios.get('/api/auth/check-auth', {
        withCredentials: true,
        timeout: 10000
      });
      
      if (response.status === 200) {
        const userData = response.data;
        
        // Tokens aus Response updaten falls vorhanden
        if (userData.accessToken) {
          setAuthToken(userData.accessToken);
        }
        if (userData.refreshToken) {
          setRefreshToken(userData.refreshToken);
        }
        
        setUserRole(userData.role);
        setClubId(userData.club);
        
        // Session Storage updaten
        sessionStorage.setItem('userRole', userData.role);
        if (userData.club) {
          sessionStorage.setItem('clubId', userData.club);
        }
        
        // ← Activity bei Auth-Check updaten (silent)
        updateActivity('auth-check');
        
        logger.info('✅ Auth check successful', { role: userData.role });
      }
    } catch (error) {       
      const err = error as { response?: { status: number } };
      
      if (err.response && err.response.status === 404 && isLoginInProgress) {
        return;
      }
      
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        if (userRole && isInitialized && !isLoginInProgress) {
          const message = err.response.status === 401 
            ? 'Deine Sitzung ist abgelaufen. Bitte melde Dich erneut an.'
            : 'Ungültiger Authentifizierungstoken. Du wirst automatisch abgemeldet.';
          toast.warning(message);
        }
        clearAuthData();
      } else if (err.response && err.response.status !== 500) {
        if (!isLoginInProgress) {
          clearAuthData();
        }
      }
    } finally {
      setLoading(false);
      setIsInitialized(true);
      localStorage.removeItem(REFRESH_KEY);
    }
  }, [lastChecked, userRole, isLoginPage, setAuthToken, setRefreshToken, clearAuthData, isInitialized, isLoginInProgress, updateActivity]);

  // ← Activity Listeners - reduzierte Frequenz
  useEffect(() => {
    if (!userRole || isLoginPage()) return;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    // ← NEU: Längeres Throttling (5 Minuten statt 1 Minute)
    let lastActivityUpdate = 0;
    const throttledUpdateActivity = () => {
      const now = Date.now();
      if (now - lastActivityUpdate > 5 * 60 * 1000) { // Max einmal alle 5 Minuten
        updateActivity('user-interaction');
        lastActivityUpdate = now;
      }
    };

    events.forEach(event => {
      document.addEventListener(event, throttledUpdateActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledUpdateActivity, true);
      });
    };
  }, [userRole, isLoginPage, updateActivity]);
  
  // Activity Check Interval
  useEffect(() => {
    if (userRole && isInitialized && !isLoginInProgress) {
      // Sofortige erste Prüfung
      checkInactivity();
      
      activityCheckInterval.current = setInterval(checkInactivity, ACTIVITY_CHECK_INTERVAL);
      
      return () => {
        if (activityCheckInterval.current) {
          clearInterval(activityCheckInterval.current);
          activityCheckInterval.current = null;
        }
      };
    }
  }, [userRole, isInitialized, isLoginInProgress, checkInactivity]);

  // Track page refreshes
  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.setItem(REFRESH_KEY, 'true');
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Initialize auth token from localStorage on app start
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      setAuthToken(token);
    }
  }, [setAuthToken]);

  // Initial auth check
  useEffect(() => {
    if (!isLoginInProgress) {
      checkAuth();
    }
  }, [isLoginInProgress]);

  // ← Response Interceptor - reduziert Spam
  useEffect(() => {    
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        // ← Entfernt: Komplexe Activity-Update Logic
        return response;
      },
      async (error) => {
        const err = error as { response?: { status: number }, config?: any };
        const isAuthEndpoint = err.config?.url?.includes('/api/auth/');
        const isRefresh = localStorage.getItem(REFRESH_KEY) === 'true';
        
        // ← VEREINFACHT: Nur ein 401-Handler, keine doppelte Logic
        if ((err.response?.status === 401 || err.response?.status === 403) && 
          !isAuthEndpoint && 
          !isRefresh && 
          isInitialized && 
          !isLoginInProgress) {

          logger.warn(`🔒 ${err.response.status} error detected, clearing auth data`, {
            url: err.config?.url,
            status: err.response.status
          });
          
          if (userRole) {
            if (err.response.status === 401) {
              toast.warning('Deine Sitzung ist abgelaufen. Du wirst zur Anmeldeseite weitergeleitet.');
            } else if (err.response.status === 403) {
              toast.warning('Ungültiger Authentifizierungstoken. Du wirst automatisch abgemeldet.');
            }
          }
          
          clearAuthData();
          
          if (!isLoginPage()) {
            navigate('/login');
          }
        }
        
        return Promise.reject(error);
      }
    );
    
    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [userRole, clearAuthData, isInitialized, isLoginInProgress, navigate, isLoginPage]);

  // Periodic auth check
  useEffect(() => {
    if (userRole && isInitialized && !isLoginInProgress) {
      const interval = setInterval(() => {
        checkAuth();
      }, 10 * 60 * 1000);
      
      return () => {
        clearInterval(interval);
      };
    }
  }, [userRole, checkAuth, isInitialized, isLoginInProgress]);

  // Login function
  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoginInProgress(true);
    setLoading(true);
    setIsInitialized(false);
    
    try {
      const response = await axios.post('/api/auth/login', { email, password }, {
        timeout: 15000
      });
      const userData = response.data;
      
      // Tokens setzen
      if (userData.accessToken) {
        setAuthToken(userData.accessToken);
      }
      
      if (userData.refreshToken) {
        setRefreshToken(userData.refreshToken);
      }
      
      // Auth-Daten setzen
      setUserRole(userData.role);
      setClubId(userData.club);
      setLastChecked(Date.now());
      
      // Session Storage updaten
      sessionStorage.setItem('userRole', userData.role);
      if (userData.club) {
        sessionStorage.setItem('clubId', userData.club);
      }
      
      // Activity nach Login setzen
      updateActivity('login');
      
      // Kurze Pause für Session-Etablierung
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Auth check nach Login
      for (let retry = 0; retry < 3; retry++) {
        try {
          await checkAuth();
          break;
        } catch (error) {
          if (retry < 2) {
            await new Promise(resolve => setTimeout(resolve, 1000 * (retry + 1)));
          }
        }
      }
      
      return true;
    } catch (error) {
      clearAuthData();
      return false;
    } finally {
      setIsLoginInProgress(false);
      setLoading(false);
      setIsInitialized(true);
    }
  };

  // Logout function
  const logout = async () => {
    setLoading(true);
    try {
      await axios.post('/api/auth/logout');
    } catch (error) {
      // Silent error handling
    } finally {
      clearAuthData();
      setLoading(false);
      setIsInitialized(true);
      navigate('/');
    }
  };

  // Register logout handler
  useEffect(() => {
    authEvents.registerLogoutHandler(logout);
    return () => {
      // Clean up if needed
    };
  }, [logout]);

  const contextValue = {
    userRole, 
    clubId, 
    loading, 
    isInitialized,
    isLoginInProgress,
    sessionTimeRemaining,
    login, 
    logout, 
    checkAuth,
    extendSession
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};